import { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '@/lib/utils';
import { Button } from '../components/ui/button';
import { Logo, LogoIcon } from '../components/Logo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Shield, 
  CreditCard, 
  AlertTriangle, 
  Check, 
  Loader2,
  ArrowLeft,
  Eye,
  EyeOff,
  Lock,
  Building2,
  FileText,
  DollarSign,
  Target,
  User,
  TrendingUp,
  Settings,
  LayoutDashboard,
  Users,
  Briefcase,
  BarChart3,
  Telescope,
  LogOut,
  Moon,
  Sun,
  Bell,
  ChevronLeft,
  ChevronRight,
  Save,
  CheckCircle,
  Globe,
  Phone,
  MapPin,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/api';

const API_BASE_URL = getApiBaseUrl();

interface UserProfile {
  role: 'founder' | 'investor' | null;
  name: string;
  email: string;
  company_name?: string;
  firm_name?: string;
  website?: string;
  location?: string;
  phone?: string;
  stage?: string;
  funding_amount?: string;
  investor_type?: string;
  check_size?: string;
  industries: string[];
  stages?: string[];
  business_description?: string;
  reachabilityScore?: number;
  reachabilityFactors?: string[];
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
}

const getNavItems = (role: string | null, isAdmin: boolean): NavItem[] => {
  const items: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/crm/dashboard' },
    { id: 'contacts', label: 'Network', icon: Users, href: '/crm/contacts', badge: 0 },
    { id: 'deals', label: 'Deal Flow', icon: Briefcase, href: '/crm/deals' },
  ];
  
  if (role === 'investor' || isAdmin) {
    items.push({ id: 'discover', label: 'Discover', icon: Telescope, href: '/crm/discover' });
  }
  
  items.push(
    { id: 'analytics', label: 'Analytics', icon: BarChart3, href: '/crm/analytics' },
    { id: 'settings', label: 'Settings', icon: Settings, href: '/crm/settings' }
  );
  
  return items;
};

export default function AccountSettings() {
  const { user, isAuthenticated, isAdmin, logout, token, isLoading: authLoading } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [editedProfile, setEditedProfile] = useState<Partial<UserProfile>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    // Wait for session restore before redirecting
    if (!authLoading && !isAuthenticated) navigate('/');
  }, [authLoading, isAuthenticated, navigate]);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditedProfile(data);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) fetchProfile();
  }, [isAuthenticated, token, fetchProfile]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedProfile),
      });
      
      if (response.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        fetchProfile();
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof UserProfile, value: any) => {
    setEditedProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : { error: await response.text() };
      
      if (!response.ok) throw new Error(data.error || 'Failed to change password');
      
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError('');
    setCancelSuccess('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/cancel-subscription`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : { error: `Unexpected response from server (${response.status})` };
      
      if (!response.ok) throw new Error(data.error || 'Failed to cancel subscription');
      
      setCancelSuccess('Your subscription has been cancelled.');
    } catch (err: any) {
      setCancelError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const calculateDaysLeft = () => {
    if (!user?.subscriptionExpiresAt) return null;
    const expiresAt = new Date(user.subscriptionExpiresAt);
    const now = new Date();
    const diffDays = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getPlanDisplayName = () => {
    if (!user?.subscriptionPlan) return 'No Plan';
    const planMap: Record<string, string> = {
      starter: 'Starter',
      fundraise: 'Fundraise Plan',
      pro: 'Pro Plan',
      '3-day': 'Starter',
      '7-day': 'Starter',
      '1-month': 'Starter',
      '6-month': 'Fundraise Plan',
      '1-year': 'Pro Plan',
      '3 Day Access': 'Starter',
      '7 Day Access': 'Starter',
      '1 Month Access': 'Starter',
      '6 Month Access': 'Fundraise Plan',
      '1 Year Access': 'Pro Plan',
    };
    return planMap[user.subscriptionPlan] || user.subscriptionPlan;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getUserInitials = () => {
    if (user?.name) return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => user?.name || user?.email?.split('@')[0] || 'User';

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  if (!user || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const daysLeft = calculateDaysLeft();
  const planName = getPlanDisplayName();
  const isInvestor = profile?.role === 'investor';

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        style={{ width: sidebarCollapsed ? 80 : 260 }}
        className={cn(
          'fixed left-0 top-0 z-50 h-screen border-r border-border bg-card flex flex-col shadow-xl'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-border px-4">
          {!sidebarCollapsed ? (
            <NavLink to="/matches" className="flex items-center gap-3 hover:opacity-80">
              <Logo size="sm" />
            </NavLink>
          ) : (
            <NavLink to="/matches" className="mx-auto hover:opacity-80">
              <LogoIcon size={36} />
            </NavLink>
          )}
          <Button variant="ghost" size="icon" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className={cn('h-8 w-8', sidebarCollapsed && 'mx-auto')}>
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-3">
            {getNavItems(profile?.role || null, isAdmin).map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
              return (
                <NavLink
                  key={item.id}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                    isActive ? 'bg-[#2B7FFF]/10 text-[#2B7FFF]' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <item.icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-[#2B7FFF]')} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <Badge variant="secondary" className="h-5 min-w-5 px-1 text-xs">{item.badge}</Badge>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        {isAdmin && !sidebarCollapsed && (
          <div className="px-4 py-2">
            <div className="flex items-center gap-2 rounded-lg bg-purple-500/10 px-3 py-2 border border-purple-500/20">
              <Shield className="h-4 w-4 text-purple-500" />
              <span className="text-xs font-medium text-purple-600">Admin Access</span>
            </div>
          </div>
        )}

        {!sidebarCollapsed && user && (
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className={cn('text-white text-sm font-semibold', isAdmin ? 'bg-purple-500' : 'bg-[#2B7FFF]')}>
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{getUserDisplayName()}</p>
                <p className="text-xs text-muted-foreground">{isAdmin ? 'Administrator' : 'Member'}</p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Header */}
      <header style={{ left: sidebarCollapsed ? 80 : 260 }} className="fixed top-0 right-0 z-40 h-16 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="flex h-full items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold">Account Settings</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleDarkMode}>
              {isDarkMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 rounded-lg px-2 gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className={cn('text-white text-sm', isAdmin ? 'bg-purple-500' : 'bg-[#2B7FFF]')}>
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium">{getUserDisplayName()}</p>
                    <p className="text-xs text-muted-foreground">{isAdmin ? 'Admin' : 'Member'}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <p className="text-sm font-medium">{getUserDisplayName()}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/crm/settings')}>
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ marginLeft: sidebarCollapsed ? 80 : 260 }} className="pt-16 min-h-screen">
        <div className="p-6 max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              <Card className="p-4 bg-primary/5 border-primary/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{isInvestor ? 'Investor Profile' : 'Founder Profile'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {isInvestor ? 'Your investment preferences and firm information' : 'Your startup information and funding details'}
                    </p>
                  </div>
                  <Badge variant="secondary" className="ml-auto">{profile?.role?.toUpperCase()}</Badge>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <User className="w-5 h-5" /> Basic Information
                  </h3>
                  <Button onClick={handleSaveProfile} disabled={isSaving} size="sm" className={cn(saveSuccess && "bg-green-600")}>
                    {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : saveSuccess ? <CheckCircle className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {saveSuccess ? 'Saved!' : 'Save'}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input value={editedProfile.name || ''} onChange={(e) => handleChange('name', e.target.value)} placeholder="Your name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={editedProfile.email || ''} disabled className="bg-muted" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={editedProfile.phone || ''} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+1 (555) 123-4567" />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={editedProfile.location || ''} onChange={(e) => handleChange('location', e.target.value)} placeholder="City, Country" />
                  </div>
                </div>
              </Card>

              {isInvestor ? (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5" /> Investment Firm Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Firm Name</Label>
                      <Input value={editedProfile.firm_name || ''} onChange={(e) => handleChange('firm_name', e.target.value)} placeholder="e.g., Accel Partners" />
                    </div>
                    <div className="space-y-2">
                      <Label>Investor Type</Label>
                      <Input value={editedProfile.investor_type || ''} onChange={(e) => handleChange('investor_type', e.target.value)} placeholder="e.g., Venture Capital" />
                    </div>
                    <div className="space-y-2">
                      <Label>Typical Check Size</Label>
                      <Input value={editedProfile.check_size || ''} onChange={(e) => handleChange('check_size', e.target.value)} placeholder="e.g., $500K - $2M" />
                    </div>
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input value={editedProfile.website || ''} onChange={(e) => handleChange('website', e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                  {profile?.reachabilityScore !== undefined && (
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-medium mb-2">Reachability Score</h4>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full" style={{ width: `${profile.reachabilityScore}%` }} />
                        </div>
                        <span className="font-semibold">{profile.reachabilityScore}/100</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Based on: {profile.reachabilityFactors?.join(', ')}</p>
                    </div>
                  )}
                </Card>
              ) : (
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5" /> Startup Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input value={editedProfile.company_name || ''} onChange={(e) => handleChange('company_name', e.target.value)} placeholder="Your company name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Current Stage</Label>
                      <Input value={editedProfile.stage || ''} onChange={(e) => handleChange('stage', e.target.value)} placeholder="e.g., Seed, Series A" />
                    </div>
                    <div className="space-y-2">
                      <Label>Funding Amount Needed</Label>
                      <Input value={editedProfile.funding_amount || ''} onChange={(e) => handleChange('funding_amount', e.target.value)} placeholder="e.g., $500K" />
                    </div>
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input value={editedProfile.website || ''} onChange={(e) => handleChange('website', e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Label>Business Description</Label>
                    <textarea
                      value={editedProfile.business_description || ''}
                      onChange={(e) => handleChange('business_description', e.target.value)}
                      placeholder="Describe your business..."
                      className="w-full min-h-[100px] px-3 py-2 rounded-md border bg-background text-sm"
                    />
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Subscription Tab */}
            <TabsContent value="subscription" className="space-y-6">
              <Card className="p-6">
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-400" /> Current Plan
                  </CardTitle>
                  <CardDescription>Your subscription details</CardDescription>
                </CardHeader>
                <CardContent className="px-0 pb-0 space-y-6">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg border">
                    <div>
                      <p className="text-sm text-muted-foreground">Plan</p>
                      <p className="text-xl font-semibold">{planName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-emerald-400 font-medium">Active</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-muted rounded-lg border">
                      <p className="text-sm text-muted-foreground mb-1">Expires On</p>
                      <p className="font-medium">{formatDate(user.subscriptionExpiresAt)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg border">
                      <p className="text-sm text-muted-foreground mb-1">Days Remaining</p>
                      <p className="font-medium">{daysLeft !== null ? `${daysLeft} days` : 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-red-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-5 h-5" /> Cancel Subscription
                  </CardTitle>
                  <CardDescription>Stop automatic renewal of your subscription</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {cancelError && (
                    <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
                      <AlertDescription>{cancelError}</AlertDescription>
                    </Alert>
                  )}
                  {cancelSuccess && (
                    <Alert className="bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                      <Check className="w-4 h-4 mr-2" />
                      <AlertDescription>{cancelSuccess}</AlertDescription>
                    </Alert>
                  )}
                  {!showCancelConfirm ? (
                    <Button variant="outline" onClick={() => setShowCancelConfirm(true)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                      Cancel Subscription
                    </Button>
                  ) : (
                    <div className="space-y-4 p-4 bg-red-500/5 rounded-lg border border-red-500/20">
                      <p className="text-foreground/80">Are you sure? You will lose access to all investor data at the end of your current billing period.</p>
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>Keep Subscription</Button>
                        <Button onClick={handleCancelSubscription} disabled={cancelLoading} className="bg-red-500/20 text-red-400 hover:bg-red-500/30">
                          {cancelLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : 'Yes, Cancel'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-6">
              <Card className="p-6">
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" /> Change Password
                  </CardTitle>
                  <CardDescription>Update your account password</CardDescription>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    {passwordError && (
                      <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
                        <AlertDescription>{passwordError}</AlertDescription>
                      </Alert>
                    )}
                    {passwordSuccess && (
                      <Alert className="bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
                        <Check className="w-4 h-4 mr-2" />
                        <AlertDescription>{passwordSuccess}</AlertDescription>
                      </Alert>
                    )}
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="pr-10"
                          placeholder="Enter current password"
                          required
                        />
                        <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pr-10"
                          placeholder="Enter new password (min 8 chars)"
                          required
                          minLength={8}
                        />
                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        required
                      />
                    </div>
                    <Button type="submit" disabled={passwordLoading} className="w-full bg-gradient-to-r from-indigo-500 to-purple-500">
                      {passwordLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : 'Change Password'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
